// Phase 9 Plan 03 Task 2 — D-03 first-wins conflict resolver.
//
// SEMANTICS:
//   Document order in `recipe.constraints[]` defines precedence: first occurrence
//   wins; later occurrences with the same conflictKey are pushed to `supersededIds`.
//
//   Single linear pass. O(V) where V ≤ constraints.length.
//
// CONFLICT-KEY PER KIND:
//   - visibility    `visibility::${show.widgetInstanceId}::${show.pathInState}`
//   - cardinality   `cardinality::${resource}::${scope}`
//   - dependency    `dependency::${if.ref.widgetInstanceId}::${if.ref.pathInState}::${JSON.stringify(if.equals)}`
//   - propagation   `propagation::${trigger.widgetInstanceId}::${trigger.pathInState}`
//   - selectability null — multiple selectability rules AND-compose per Q-05; the
//                   dispatcher (Plan 09-06) evaluates each independently and the
//                   resolver does NOT mark any as superseded.
//
// Q-05 (selectability): selectability constraints AND-compose (all must permit);
//   they do NOT participate in first-wins resolution. Plan 09-06 evaluator dispatches
//   all selectability constraints independently. resolveConflicts returns ZERO
//   conflicts for selectability even when multiple coexist with the same recipeId.
//
// PARANOID-STRICT:
//   - Compile-time exhaustiveness via `_exhaustive: never` in switch defaults.
//   - JSON.stringify on dependency.if.equals (unknown values) — safe stringification
//     for object/primitive equals values; key collision behavior matches D-03.

import type { Constraint } from "../schemas/constraint.js";
import type { ConstraintConflict } from "./types.js";

/**
 * D-03 conflict resolution. Single linear pass over `constraints[]` in document order.
 *
 * Returns ConstraintConflict[] enumerating each (winning constraint id, list of
 * superseded ids). Conflicts are NOT validation errors — they are informational and
 * surface at eval-time. Per Plan 09-06, the dispatcher consumes this to mark verdicts
 * `status: "superseded"` on the loser constraints.
 *
 * O(V) — linear pass + O(1) map lookup per constraint.
 */
export function resolveConflicts(
  constraints: ReadonlyArray<Constraint>,
): ReadonlyArray<ConstraintConflict> {
  const seen = new Map<
    string,
    {
      winningIdx: number;
      supersededIds: string[];
      reason: ConstraintConflict["reason"];
    }
  >();

  for (let i = 0; i < constraints.length; i++) {
    const c = constraints[i];
    if (c === undefined) continue;
    const id = `constraint-${i}`;
    const key = conflictKey(c);
    if (key === null) continue; // Q-05 — selectability AND-composes, never conflicts.

    const existing = seen.get(key);
    if (existing === undefined) {
      seen.set(key, {
        winningIdx: i,
        supersededIds: [],
        reason: reasonFor(c.kind),
      });
    } else {
      existing.supersededIds.push(id);
    }
  }

  const out: ConstraintConflict[] = [];
  for (const entry of seen.values()) {
    if (entry.supersededIds.length === 0) continue;
    const winner = constraints[entry.winningIdx];
    if (winner === undefined) continue; // noUncheckedIndexedAccess defensive narrow
    out.push({
      winningId: `constraint-${entry.winningIdx}`,
      winningKind: winner.kind,
      supersededIds: entry.supersededIds,
      reason: entry.reason,
    });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Internal helpers (not exported — barrel skips these).
// ---------------------------------------------------------------------------

function conflictKey(c: Constraint): string | null {
  switch (c.kind) {
    case "visibility":
      return `visibility::${c.show.widgetInstanceId}::${c.show.pathInState}`;
    case "cardinality":
      return `cardinality::${c.resource}::${c.scope}`;
    case "dependency":
      return `dependency::${c.if.ref.widgetInstanceId}::${c.if.ref.pathInState}::${JSON.stringify(
        c.if.equals,
      )}`;
    case "propagation":
      return `propagation::${c.trigger.widgetInstanceId}::${c.trigger.pathInState}`;
    case "selectability":
      // Q-05 — selectability AND-composes (all must permit). The dispatcher
      // evaluates each independently; the conflict resolver does NOT mark any as
      // superseded. Returns null to skip the conflict-map entry.
      return null;
    default: {
      // Compile-time exhaustiveness — adding a 6th kind fails the typecheck.
      const _exhaustive: never = c;
      void _exhaustive;
      return null;
    }
  }
}

function reasonFor(kind: Constraint["kind"]): ConstraintConflict["reason"] {
  switch (kind) {
    case "visibility":
      return "overlapping-visibility";
    case "cardinality":
      return "overlapping-cardinality";
    case "dependency":
      return "overlapping-dependency";
    case "propagation":
      // Closest semantic — propagation conflicts are scope-overlap in spirit.
      return "overlapping-cardinality";
    case "selectability":
      // Unreachable per conflictKey null branch above; the reason union doesn't
      // carry a selectability variant. Return the closest neutral reason.
      return "overlapping-cardinality";
    default: {
      const _exhaustive: never = kind;
      void _exhaustive;
      return "overlapping-cardinality";
    }
  }
}
